import React, { useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { ModalFlatList } from 'design-system/ModalScrollables';
import Modal from 'utils/packages/Modal';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { rem } from 'design-system/values';
import Text from 'design-system/Text';
import { SelectableAccountCard } from 'design-system/SelectableAccountCard';
import { useAppSelector } from 'store/hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useScrollableModalMaxHeight } from 'hooks/useScrollableModalMaxHeight';
import { selectGBAccountsWithPISPInfo } from 'reducers/selectors';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import PopUpMenuModal, { defaultPopUpWidth } from 'design-system/PopUpMenuModal.web';
import { isWeb } from '../../../constants';
const strings = { modalTitle: 'Pick an account' };
const HEIGHT = rem(76 + 16);
const keyExtractor = (item) => item.id.toString();
const getItemLayout = (_, index) => ({
    length: rem(HEIGHT),
    offset: rem(HEIGHT) * index,
    index,
});
const useAccountPicker = () => {
    const { accounts: allBankAccounts } = useAppSelector(selectGBAccountsWithPISPInfo);
    const { navigateDeprecated } = useMainStackNavigation();
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING - rem(16));
    const modalMaxHeight = useScrollableModalMaxHeight();
    const scrollY = useSharedValue(0);
    const onScroll = useAnimatedScrollHandler((e) => {
        scrollY.value = e.contentOffset.y;
    }, []);
    const openAccountPicker = useCallback((bankAccounts, params, touchViewRef) => {
        const accounts = bankAccounts || allBankAccounts;
        if (accounts.length || params?.emptyText) {
            let isResolved = false;
            return new Promise((resolve, reject) => {
                const subTitle = params?.subTitle ? (<Text TextThin-14 Gray centered style={styles.subTitle}>
              {params.subTitle}
            </Text>) : null;
                const onConnectAccount = () => {
                    reject(new Error());
                    (isWeb ? PopUpMenuModal : Modal).hide(() => {
                        navigateDeprecated('ChooseBank', { back: true, investments: false });
                    });
                };
                const connectButton = (<Button brandReversed floating title="Connect another account" onPress={onConnectAccount}/>);
                const selectedIdsSet = new Set(params?.selectedIds);
                const handleAccountPress = (account) => {
                    isResolved = true;
                    resolve(account);
                    (isWeb ? PopUpMenuModal : Modal).hide();
                };
                const handleOpenLogins = () => {
                    isResolved = true;
                    reject(new Error());
                    (isWeb ? PopUpMenuModal : Modal).hide(() => {
                        navigateDeprecated('Logins');
                    });
                };
                const renderItem = ({ item: account }) => {
                    const isUnsupported = params?.disableNonPISP !== false && !('pispBankId' in account && account.pispBankId);
                    return (<SelectableAccountCard alt data={account} white hideSpaceUserAvatar isMultipleSelect={false} onPress={() => handleAccountPress(account)} type="single" isSelected={selectedIdsSet.has(account.id)} hideActionRequiredText disabled={isUnsupported} notSupportedText={isUnsupported ? 'Not supported' : undefined} onOpenLogins={handleOpenLogins}/>);
                };
                const showConnectButton = !params?.hideConnectNewAccountView;
                const content = !accounts.length ? (<>
              <View style={styles.emptyContainer}>
                <Text Text-16 centered>
                  {params?.emptyText}
                </Text>
              </View>
              {showConnectButton ? connectButton : null}
            </>) : (<>
              <ModalFlatList style={{ maxHeight: modalMaxHeight }} data={accounts} initialNumToRender={7} windowSize={10} maxToRenderPerBatch={10} keyExtractor={keyExtractor} contentContainerStyle={isWeb ? undefined : paddingBottom} onScroll={onScroll} scrollEventThrottle={16} getItemLayout={getItemLayout} renderItem={renderItem}/>
              {showConnectButton ? connectButton : null}
            </>);
                const title = (<View style={styles.title}>
              <Text Text-20 centered>
                {params?.title || strings.modalTitle}
              </Text>
              {subTitle}
            </View>);
                if (isWeb) {
                    touchViewRef?.current?.measure((_, __, width, height, fx, fy) => {
                        PopUpMenuModal.show(content, {
                            onDidHideCallback: () => {
                                if (!isResolved) {
                                    reject(new Error());
                                }
                            },
                            containerStyle: styles.modalContainerStyle,
                            fx: fx + width - defaultPopUpWidth,
                            fy: fy + height - rem(8),
                        });
                    });
                }
                Modal.show(content, {
                    title,
                    onDidHideCallback: () => {
                        if (!isResolved) {
                            reject(new Error());
                        }
                    },
                    scrollable: {
                        scrollValue: scrollY,
                    },
                    containerStyle: styles.modalContainerStyle,
                });
            });
        }
        if (!params?.emptyText) {
            navigateDeprecated('ChooseBank', { back: true });
        }
        throw new Error();
    }, [allBankAccounts, modalMaxHeight, onScroll, paddingBottom, scrollY]);
    return openAccountPicker;
};
const styles = StyleSheet.create({
    emptyContainer: {
        marginTop: rem(36),
        paddingHorizontal: rem(32),
    },
    title: {
        marginTop: rem(36),
        marginBottom: rem(8),
    },
    subTitle: {
        marginTop: rem(12),
    },
    modalContainerStyle: {
        ...(!isWeb && {
            paddingHorizontal: 0,
        }),
    },
});
export default useAccountPicker;
